import React, { Fragment, useEffect, useState, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import LoadingContext from '../../context/Loading/Loading'
import { ModalsProvider } from '../../components/PurchaseFlow/NewCheckoutWrapper/providers/ModalsProvider'

import { getOrganizationStorefront } from '../../utilities/api'

import { PageLoadingContainer, OrganizationLanding } from '../../components'
import NotFoundPage from '../NotFoundPage/NotFoundPage'

import { initializeTracking } from '../../utilities/tracking'
import { cacheOrgEventBranding } from '../../utilities/orgBrandingCache'

export default function OrganizationPage() {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { slug } = useParams()

  const [organization, setOrganization] = useState()
  const [venues, setVenues] = useState([])
  const [events, setEvents] = useState([])
  const [packages, setPackages] = useState([])
  const [flexPacks, setFlexPacks] = useState([])

  useEffect(() => {
    showLoading()
    // Switching teams reuses this component, so the previous tenant is still in
    // state. Clear it or the loader spends the whole fetch showing their brand.
    setOrganization(undefined)
    setVenues([])
    setEvents([])
    setPackages([])
    setFlexPacks([])

    getOrganizationStorefront(slug)
      .then((res) => {
        const data = res.data
        if (!data?.organization) {
          setOrganization(null)
          return
        }

        initializeTracking(data.organization)
        cacheOrgEventBranding(data.events, data.organization)
        setOrganization(data.organization)
        setVenues(data.venues || [])
        setEvents(data.events || [])
        setPackages(data.packages || [])
        setFlexPacks(data.flexPacks || [])
      })
      .catch((err) => {
        console.error(err)
        setOrganization(null)
      })
      .finally(() => {
        // Brief handoff so the tenant loader can paint with cached branding.
        setTimeout(() => hideLoading(), 700)
      })
  }, [slug])

  const pageTitle = organization?.name
    ? `${organization.name} | Tickets & Passes`
    : 'Tickets & Passes'

  const themeColor =
    organization?.branding?.primaryColor ||
    organization?.primaryColor ||
    '#051B35'

  return (
    <Fragment>
      <Helmet>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
        <meta name="theme-color" content={themeColor} />
        <meta
          name="keywords"
          content={`Buy tickets, season passes, and flex packs for ${organization?.name || 'events'}`}
        />
        <title>{pageTitle}</title>
        <meta property="og:url" content={`${window.location.href}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta
          property="og:description"
          content={`Browse and buy tickets, season passes, and flex packs for ${organization?.name || 'this organization'}.`}
        />
        <meta property="og:image" content={organization?.image?.url} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content={`${window.location.host}`} />
        <meta property="twitter:url" content={`${window.location.href}`} />
        <meta name="twitter:title" content={pageTitle} />
        <meta
          name="twitter:description"
          content={`Browse and buy tickets, season passes, and flex packs for ${organization?.name || 'this organization'}.`}
        />
        <meta name="twitter:image" content={organization?.image?.url} />
      </Helmet>
      {isLoading ? (
        <PageLoadingContainer organization={organization} />
      ) : organization === null ? (
        <NotFoundPage />
      ) : (
        <section>
          <ModalsProvider>
            <OrganizationLanding
              organization={organization}
              venues={venues}
              events={events}
              packages={packages}
              flexPacks={flexPacks}
              basePath={`/${slug}`}
            />
          </ModalsProvider>
        </section>
      )}
    </Fragment>
  )
}
